import { useState, useMemo, useRef, useEffect, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import {
    Eye, Volume2, CheckCheck, Play, Pause, MapPin, User, CircleUser,
    Download, File, FileText, Video, Music, ChevronDown, Clock, AlertCircle, RotateCw,
} from "lucide-react";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import type { ChatMessage } from "@/lib/types";
import { TranslatedMessage } from "./TranslatedMessage";
import { EmojiReactionBar } from "./EmojiReactionBar";

// ---------------------------------------------------------------------------
// Module-level audio singleton — stops previous playback when a new one starts
// ---------------------------------------------------------------------------
let _activeAudio: HTMLAudioElement | null = null;
let _activeStopCallback: (() => void) | null = null;

function claimAudio(audio: HTMLAudioElement, onStopped: () => void) {
    if (_activeAudio && _activeAudio !== audio) {
        _activeAudio.pause();
        _activeAudio.currentTime = 0;
        _activeStopCallback?.();
    }
    _activeAudio = audio;
    _activeStopCallback = onStopped;
}

function releaseAudio(audio: HTMLAudioElement) {
    if (_activeAudio === audio) {
        _activeAudio = null;
        _activeStopCallback = null;
    }
}

// ---------------------------------------------------------------------------
// Reliable file download — fetch as blob so the browser saves instead of
// navigating. Works for cross-origin URLs (Supabase Storage) because the
// bucket serves CORS headers, and for legacy same-origin /uploads URLs.
// ---------------------------------------------------------------------------
async function downloadFile(fileUrl: string, filename: string) {
    try {
        // Use the full URL as-is. Supabase Storage public URLs return
        // `access-control-allow-origin: *`, so `fetch()` works cross-origin
        // and we can convert to a blob URL for a proper save-as download.
        const res = await fetch(fileUrl);
        if (!res.ok) throw new Error("fetch failed");
        const blob = await res.blob();
        const blobUrl = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = blobUrl;
        a.download = filename || "download";
        a.style.display = "none";
        document.body.appendChild(a);
        a.click();
        // Small delay before cleanup to ensure download starts
        setTimeout(() => {
            document.body.removeChild(a);
            URL.revokeObjectURL(blobUrl);
        }, 100);
    } catch {
        // Fallback: direct anchor download. The browser may navigate instead
        // of saving if the server doesn't set Content-Disposition, but at
        // least the user reaches the asset.
        const a = document.createElement("a");
        a.href = fileUrl;
        a.download = filename || "download";
        a.target = "_blank";
        a.rel = "noopener noreferrer";
        a.style.display = "none";
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
    }
}

// ---------------------------------------------------------------------------
// TTS helper using the Web Speech API
// ---------------------------------------------------------------------------
function speakText(text: string, lang: string) {
    if (!("speechSynthesis" in window)) return;
    window.speechSynthesis.cancel();
    const utt = new SpeechSynthesisUtterance(text);
    utt.lang = lang;
    window.speechSynthesis.speak(utt);
}

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------
interface MessageBubbleProps {
    message: ChatMessage;
    isOwn: boolean;
    showTranslation: boolean;
    onToggleOriginal: () => void;
    onPlayAudio?: () => void;
    onDelete?: () => void;
    userLanguage?: string;
    currentUserId?: string;
    onReact?: (messageId: string, emoji: string) => void;
    onUnreact?: (messageId: string, emoji: string) => void;
    onResend?: () => void;
}

// ---------------------------------------------------------------------------
// MessageBubble
// ---------------------------------------------------------------------------
export function MessageBubble({
    message,
    isOwn,
    showTranslation,
    onToggleOriginal,
    onDelete,
    userLanguage = "en",
    currentUserId = "",
    onReact,
    onUnreact,
    onResend,
}: MessageBubbleProps) {
    const [showOriginal, setShowOriginal] = useState(false);
    const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);

    const formatTime = (timestamp: Date | string) =>
        new Date(timestamp).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });

    const isSeen = Boolean(
        (message.readBy && message.readBy.some((uid) => uid !== message.senderId)) ||
        message.deliveryStatus === 'read'
    );

    const toggleOriginal = () => {
        setShowOriginal((prev) => !prev);
        onToggleOriginal();
    };

    if (message.messageType === "voice") {
        return <VoiceMessageBubble message={message} isOwn={isOwn} formatTime={formatTime} onDelete={onDelete} />;
    }

    if (message.messageType === "location") {
        return (
            <div className={`flex ${isOwn ? "justify-end" : "items-start space-x-2"}`}>
                {!isOwn && (
                    <Avatar className="w-8 h-8 flex-shrink-0">
                        <AvatarFallback className="bg-gradient-to-br from-[var(--avatar-ring-1)] to-[var(--avatar-ring-2)] text-white">
                            <User className="w-4 h-4" />
                        </AvatarFallback>
                    </Avatar>
                )}
                <div className={`max-w-xs ${isOwn ? "" : "flex-1"}`}>
                    <div className={`rounded-2xl overflow-hidden shadow-md ${isOwn ? "message-bubble-own" : "message-bubble-other"}`}>
                        <div className="h-32 bg-gradient-to-br from-blue-400 to-green-400 relative flex items-center justify-center">
                            <div className="absolute inset-0 bg-black bg-opacity-20" />
                            <MapPin className="w-8 h-8 text-white relative z-10" />
                        </div>
                        <div className="p-3">
                            <p className="text-sm font-medium">Current Location</p>
                            <p className={`text-xs ${isOwn ? "text-white/70" : "text-muted-foreground"}`}>{message.content}</p>
                            <Button variant="ghost" size="sm" className={`mt-2 text-xs underline p-0 h-auto ${isOwn ? "text-white hover:text-white/80" : ""}`}>
                                View in Maps
                            </Button>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    const hasMedia =
        message.attachments &&
        message.attachments.length > 0 &&
        message.attachments.some((att) => att.mimetype.startsWith("image/") || att.mimetype.startsWith("video/"));

    // The text to read aloud: prefer displayed translation, fall back to original
    const ttsText = showOriginal
        ? (message.originalContent || message.content)
        : (message.content || "");

    return (
        <div className={`group flex ${isOwn ? "justify-end" : "items-start space-x-2"}`}>
            <div className={`flex flex-col space-y-1 max-w-[85%] md:max-w-md ${isOwn ? "items-end" : "items-start"}`}>
                <div
                    className={`${hasMedia ? "p-2" : "p-4"}   mr-4 inline-block break-words dark:bg-[#0f172a] bg-[#f8fafc] ${
                        isOwn
                            ? "message-bubble-own shadow-[4px_4px_0_0_var(--bubble-own-from)] rounded-tl-[30px] rounded-bl-[30px] rounded-br-[30px]"
                            : "message-bubble-other shadow-[4px_4px_0_0_var(--bubble-own-to)] rounded-tr-[30px] rounded-bl-[30px] rounded-br-[30px]"
                    }`}
                >
                    <div className="flex flex-col w-full gap-1">
                        {/* Header row: sender name + action buttons */}
                        <div className={`${hasMedia ? "pt-2 pl-2" : ""} flex justify-between gap-3 items-start w-full`}>
                            <div className="flex items-center">
                                {/* <CircleUser className="w-5 h-5" /> */}
                               <span
  className={`text-[14px] ${
    isOwn ? "text-[var(--bubble-own-from)]" : "text-[var(--bubble-own-to)]"
  }`}
>
  {isOwn ? "You" : (message?.senderName || "Anonymous")}
</span>
                            </div>

                            <div className="flex items-center space-x-1">
                                {/* Eye + Speaker icons for all received messages (translation may be server-side or client-side) */}
                                {!isOwn && message.content?.trim() && (
                                    <>
                                        {/* <Button
                                            variant="ghost"
                                            size="icon"
                                            className="w-6 h-6 p-0 hover:bg-transparent"
                                            onClick={toggleOriginal}
                                            title={showOriginal ? "Show translation" : "Show original"}
                                        >
                                            <Eye className={`w-4 h-4 ${showOriginal ? "text-[var(--accent)]" : "text-muted-foreground hover:text-[var(--accent)]"}`} />
                                        </Button> */}
                                        <Button
                                            variant="ghost"
                                            size="icon"
                                            className="w-6 h-6 p-0 hover:bg-transparent flex justify-end items-center"
                                            onClick={() => speakText(
                                                ttsText,
                                                showOriginal
                                                    ? (message.detectedLanguage || userLanguage)
                                                    : userLanguage,
                                            )}
                                            title="Read aloud"
                                        >
                                            <Volume2 className="w-4 h-4 text-[var(--bubble-own-to)]" />
                                        </Button>
                                    </>
                                )}
                                {isOwn && (
                                    <DropdownMenu>
                                        <DropdownMenuTrigger asChild>
                                            <Button variant="ghost" size="icon" className={`w-6 h-6 p-0 ${
    isOwn ? "text-[var(--bubble-own-from)] hover:bg-[var(--bubble-own-from)]" : "text-[var(--bubble-own-to)] hover:bg-[var(--bubble-own-to)]"
  } hover:text-[#faf8fc] `}>
                                                <ChevronDown className="w-4 h-4 " />
                                            </Button>
                                        </DropdownMenuTrigger>
                                        <DropdownMenuContent align="end">
                                            <DropdownMenuItem
                                                onSelect={(e) => {
                                                    e.preventDefault();
                                                    setConfirmDeleteOpen(true);
                                                }}
                                                className="justify-center hover:text-white cursor-pointer"
                                            >
                                                Delete
                                            </DropdownMenuItem>
                                        </DropdownMenuContent>
                                    </DropdownMenu>
                                )}
                            </div>
                        </div>

                        {/* Attachments. When there are multiple media attachments
                            (>= 2 images/videos), arrange them in a compact grid
                            instead of stacking full-width — closer to the
                            WhatsApp/iMessage layout that QA asked for. */}
                        {message.attachments && message.attachments.length > 0 ? (
                            <div
                                className={
                                    hasMedia && message.attachments.filter((a) => a.mimetype.startsWith("image/") || a.mimetype.startsWith("video/")).length >= 2
                                        ? `grid gap-1 ${message.attachments.length === 2 ? "grid-cols-2" : message.attachments.length === 3 ? "grid-cols-2" : "grid-cols-2"} max-w-xs`
                                        : hasMedia ? "" : "space-y-2 mb-2"
                                }
                            >
                                {message.attachments.map((attachment, index) => {
                                    const isImage = attachment.mimetype.startsWith("image/");
                                    const isVideo = attachment.mimetype.startsWith("video/");
                                    const mediaCount = message.attachments!.filter((a) => a.mimetype.startsWith("image/") || a.mimetype.startsWith("video/")).length;
                                    const isGrid = hasMedia && mediaCount >= 2;
                                    // First attachment in an odd-count grid (e.g. 3 files) spans both columns
                                    // for a balanced 1-on-top / 2-below layout.
                                    const spanBoth = isGrid && mediaCount === 3 && index === 0;
                                    return (
                                        <div
                                            key={index}
                                            className={
                                                isGrid
                                                    ? `relative ${spanBoth ? "col-span-2" : ""}`
                                                    : hasMedia ? "" : "max-w-xs"
                                            }
                                        >
                                            {isImage ? (
                                                <div className={`relative group ${hasMedia ? "rounded-xl" : "rounded-lg"} overflow-hidden ${isGrid ? "aspect-square" : ""}`}>
                                                    <img
                                                        src={attachment.url}
                                                        alt={attachment.originalName}
                                                        className={`w-full ${isGrid ? "h-full" : "h-auto max-h-64"} object-cover cursor-pointer`}
                                                        onClick={() => window.open(attachment.url, "_blank")}
                                                        onLoad={() => {
                                                            setTimeout(() => {
                                                                document.querySelector("[data-messages-end]")?.scrollIntoView({ behavior: "smooth" });
                                                            }, 50);
                                                        }}
                                                    />
                                                    <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-20 transition-all duration-200 flex items-center justify-center">
                                                        <div className="opacity-0 group-hover:opacity-100 flex space-x-2">
                                                            <Button
                                                                size="sm"
                                                                variant="secondary"
                                                                onClick={() => downloadFile(attachment.url, attachment.originalName)}
                                                                className="bg-white/90 hover:bg-white text-gray-800"
                                                            >
                                                                <Download className="w-4 h-4 mr-1" />
                                                                Save
                                                            </Button>
                                                        </div>
                                                    </div>
                                                    <div className="absolute bottom-2 left-2 bg-black/70 text-white text-xs px-2 py-1 rounded">
                                                        {(attachment.size / 1024 / 1024).toFixed(1)} MB
                                                    </div>
                                                </div>
                                            ) : isVideo ? (
                                                <div className={`relative group ${hasMedia ? "rounded-xl" : "rounded-lg"} overflow-hidden`}>
                                                    <div className="w-full flex justify-center h-64 max-h-64">
                                                        <div className="relative w-full max-w-xs aspect-video rounded-lg overflow-hidden bg-gray-200">
                                                            <video
                                                                src={attachment.url}
                                                                className="w-full h-full object-cover cursor-pointer"
                                                                controls
                                                                preload="metadata"
                                                                onLoadedData={() => {
                                                                    setTimeout(() => {
                                                                        document.querySelector("[data-messages-end]")?.scrollIntoView({ behavior: "smooth" });
                                                                    }, 100);
                                                                }}
                                                            >
                                                                Your browser does not support the video tag.
                                                            </video>
                                                        </div>
                                                    </div>
                                                    <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-20 transition-all duration-200 flex items-center justify-center pointer-events-none">
                                                        <div className="opacity-0 group-hover:opacity-100 flex space-x-2 pointer-events-auto">
                                                            <Button
                                                                size="sm"
                                                                variant="secondary"
                                                                onClick={() => downloadFile(attachment.url, attachment.originalName)}
                                                                className="bg-white/90 hover:bg-white text-gray-800"
                                                            >
                                                                <Download className="w-4 h-4 mr-1" />
                                                                Save
                                                            </Button>
                                                        </div>
                                                    </div>
                                                    <div className="absolute bottom-2 left-2 bg-black/70 text-white text-xs px-2 py-1 rounded">
                                                        {(attachment.size / 1024 / 1024).toFixed(1)} MB
                                                    </div>
                                                </div>
                                            ) : (
                                                <div className={`border rounded-lg p-3 ${isOwn ? "bg-[var(--accent)]/5 border-[var(--accent)]/20" : "bg-gray-50 dark:bg-gray-800 border-gray-200 dark:border-gray-700"}`}>
                                                    <div className="flex items-center space-x-3">
                                                        <div className={`p-2 rounded-full ${isOwn ? "bg-[var(--accent)]/10" : "bg-blue-100 dark:bg-blue-900"}`}>
                                                            <div className={isOwn ? "text-[var(--accent)]" : "text-blue-600 dark:text-blue-400"}>
                                                                {attachment.mimetype.startsWith("video/") ? <Video className="w-4 h-4" /> :
                                                                    attachment.mimetype.startsWith("audio/") ? <Music className="w-4 h-4" /> :
                                                                        attachment.mimetype.includes("pdf") || attachment.mimetype.includes("document") ? <FileText className="w-4 h-4" /> :
                                                                            <File className="w-4 h-4" />}
                                                            </div>
                                                        </div>
                                                        <div className="flex-1 min-w-0">
                                                            <p className="text-sm font-medium truncate">{attachment.originalName}</p>
                                                            <p className="text-xs opacity-60">{(attachment.size / 1024 / 1024).toFixed(1)} MB</p>
                                                        </div>
                                                        <div className="flex space-x-1">
                                                            <Button
                                                                size="sm"
                                                                variant="ghost"
                                                                onClick={() => window.open(attachment.url, "_blank")}
                                                                className={`h-8 w-8 p-0 ${isOwn ? "text-white/80 hover:text-white hover:bg-white/10" : "text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"}`}
                                                            >
                                                                <Eye className="w-4 h-4" />
                                                            </Button>
                                                            <Button
                                                                size="sm"
                                                                variant="ghost"
                                                                onClick={() => downloadFile(attachment.url, attachment.originalName)}
                                                                className={`h-8 w-8 p-0 ${isOwn ? "text-white/80 hover:text-white hover:bg-white/10" : "text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"}`}
                                                            >
                                                                <Download className="w-4 h-4" />
                                                            </Button>
                                                        </div>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        ) : message.messageType === "file" ? (
                            <div className="p-3 border rounded bg-gray-100 dark:bg-gray-800 text-sm text-gray-600 dark:text-gray-400">📎 File attachment (loading...)</div>
                        ) : null}

                        {/* Text content */}
                        {message.content && message.content.trim() && (
                            <div className={`flex flex-row justify-start items-start gap-2 ${hasMedia ? "p-2" : ""}`}>
                                <div className="max-w-[90%] flex-shrink break-words">
                                    {showTranslation ? (
                                        <TranslatedMessage
                                            message={message}
                                            userLanguage={userLanguage}
                                            isOwn={isOwn}
                                            showTranslation={showTranslation}
                                            showOriginal={showOriginal}
                                            onToggleOriginal={toggleOriginal}
                                        />
                                    ) : (
                                        <p className="text-sm leading-relaxed whitespace-pre-wrap break-words [word-break:break-word] hyphens-auto">
                                            {message.originalContent || message.content}
                                        </p>
                                    )}
                                </div>
                            </div>
                        )}

                        <div className="flex items-end justify-end  gap-2 pb-0">
                            <span className={`text-[12px] ${isOwn ? "opacity-60 text-[var(--bubble-own-from)]" : " text-[var(--bubble-own-to)] "} mt-0.5`}>
                                {formatTime(message.timestamp)}
                            </span>
                            <div className="justify-center align-center">
                                {isOwn && message.status === "sending" && (
                                    <Clock
                                        className="w-4 h-4 opacity-60 mb-1 text-[var(--bubble-own-from)] animate-pulse"
                                        aria-label="Sending"
                                    />
                                )}
                                {isOwn && message.status === "failed" && (
                                    <button
                                        type="button"
                                        onClick={onResend}
                                        className="flex items-center gap-0.5 mb-1 text-red-500 hover:text-red-600"
                                        aria-label="Message failed to send. Tap to retry."
                                        title="Failed to send — tap to retry"
                                    >
                                        <AlertCircle className="w-4 h-4" />
                                        <RotateCw className="w-3.5 h-3.5" />
                                    </button>
                                )}
                                {isOwn && message.status !== "sending" && message.status !== "failed" && (
                                    <span title={isSeen ? "Seen" : "Delivered"}>
                                        <CheckCheck
                                            className={`w-5 h-5 mb-1 transition-colors duration-300 ${
                                                isSeen
                                                    ? "text-[var(--bubble-own-from)] opacity-100"
                                                    : "text-gray-400 dark:text-gray-500 opacity-60"
                                            }`}
                                            aria-label={isSeen ? "Seen" : "Delivered"}
                                        />
                                    </span>
                                )}
                            </div>
                        </div>
                    </div>
                </div>

                {/* Emoji reactions */}
                {onReact && onUnreact && (
                    <EmojiReactionBar
                        messageId={message.messageId}
                        reactions={message.reactions}
                        currentUserId={currentUserId}
                        onReact={onReact}
                        onUnreact={onUnreact}
                    />
                )}
            </div>
            <DeleteMessageDialog
                open={confirmDeleteOpen}
                onOpenChange={setConfirmDeleteOpen}
                onConfirm={() => {
                    setConfirmDeleteOpen(false);
                    onDelete?.();
                }}
            />
        </div>
    );
}

function DeleteMessageDialog({
    open,
    onOpenChange,
    onConfirm,
}: {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    onConfirm: () => void;
}) {
    return (
        <AlertDialog open={open} onOpenChange={onOpenChange}>
            <AlertDialogContent>
                <AlertDialogHeader>
                    <AlertDialogTitle>Delete message?</AlertDialogTitle>
                    <AlertDialogDescription>
                        This message will be removed for everyone in the chat. This action cannot be undone.
                    </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                    <AlertDialogCancel>Cancel</AlertDialogCancel>
                    <AlertDialogAction
                        onClick={onConfirm}
                        className="bg-red-600 hover:bg-red-700 text-white"
                    >
                        Delete
                    </AlertDialogAction>
                </AlertDialogFooter>
            </AlertDialogContent>
        </AlertDialog>
    );
}

// ---------------------------------------------------------------------------
// VoiceMessageBubble
// ---------------------------------------------------------------------------
function VoiceMessageBubble({
    message,
    isOwn,
    formatTime,
    onDelete,
}: {
    message: ChatMessage;
    isOwn: boolean;
    formatTime: (ts: Date | string) => string;
    onDelete?: () => void;
}) {
    const audioRef = useRef<HTMLAudioElement | null>(null);
    const [isPlaying, setIsPlaying] = useState(false);
    const [progress, setProgress] = useState(0);
    const [audioDuration, setAudioDuration] = useState<number | null>(null);
    const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
    const animFrameRef = useRef<number>(0);

    const audioUrl = message.attachments?.[0]?.url;

    // Preload metadata so the first play has a valid duration. Without this,
    // the Audio element doesn't fetch metadata until play(), so the progress
    // bar denominator is NaN until the first `loadedmetadata` event fires.
    useEffect(() => {
        if (!audioUrl || audioRef.current) return;
        const audio = new Audio();
        audio.preload = "metadata";
        audio.src = audioUrl;
        audioRef.current = audio;
        const onLoaded = () => {
            if (audio.duration && isFinite(audio.duration)) {
                setAudioDuration(audio.duration);
            }
        };
        audio.addEventListener("loadedmetadata", onLoaded);
        return () => {
            audio.removeEventListener("loadedmetadata", onLoaded);
        };
    }, [audioUrl]);

    const fmtSec = (s: number) => {
        const m = Math.floor(s / 60);
        const sec = Math.floor(s % 60);
        return `${m}:${sec.toString().padStart(2, "0")}`;
    };

    // Deterministic waveform heights seeded from message id — gives each voice
    // message a unique-looking but stable waveform.
    const barCount = 28;
    const bars = useMemo(() => {
        const seed = (message.messageId || message.id || "x")
            .split("")
            .reduce((acc, ch) => acc + ch.charCodeAt(0), 0);
        return Array.from({ length: barCount }, (_, i) => {
            const t = (i / barCount) * Math.PI * 2;
            const h = 5 + Math.abs(Math.sin(t * 3.1 + seed * 0.07) * 10)
                + Math.abs(Math.cos(t * 1.7 + seed * 0.13) * 6);
            return Math.max(3, Math.round(h));
        });
    }, [message.messageId, message.id]);

    const maxBarH = Math.max(...bars);

    const updateProgress = useCallback(() => {
        const audio = audioRef.current;
        if (audio && audio.duration && isFinite(audio.duration)) {
            setProgress(audio.currentTime / audio.duration);
        }
        if (audioRef.current && !audioRef.current.paused) {
            animFrameRef.current = requestAnimationFrame(updateProgress);
        }
    }, []);

    useEffect(() => {
        return () => {
            if (animFrameRef.current) cancelAnimationFrame(animFrameRef.current);
            if (audioRef.current) {
                audioRef.current.pause();
                releaseAudio(audioRef.current);
            }
        };
    }, []);

    const stopPlayback = useCallback(() => {
        setIsPlaying(false);
        setProgress(0);
        if (animFrameRef.current) cancelAnimationFrame(animFrameRef.current);
    }, []);

    const togglePlay = useCallback(() => {
        if (!audioUrl) return;

        // The audio element is created eagerly in the preload effect so
        // metadata is ready by the first play. If for some reason it's not
        // there yet (e.g. URL changed mid-render), bail and let the effect
        // catch up.
        const audio = audioRef.current;
        if (!audio) return;

        if (!audio.dataset.endedAttached) {
            audio.addEventListener("ended", () => {
                stopPlayback();
                releaseAudio(audio);
            });
            audio.dataset.endedAttached = "1";
        }

        if (audio.paused) {
            claimAudio(audio, stopPlayback);
            audio.play();
            setIsPlaying(true);
            animFrameRef.current = requestAnimationFrame(updateProgress);
        } else {
            audio.pause();
            setIsPlaying(false);
            if (animFrameRef.current) cancelAnimationFrame(animFrameRef.current);
            releaseAudio(audio);
        }
    }, [audioUrl, updateProgress, stopPlayback]);

    const displayDuration = audioDuration
        ? fmtSec(audioDuration)
        : (message.metadata?.duration || null);

    const currentTimeLabel = isPlaying && audioRef.current
        ? fmtSec(audioRef.current.currentTime)
        : (displayDuration ?? "—");

    return (
        <div className={`flex ${isOwn ? "justify-end" : "items-start"}`}>
            <div className={`${isOwn ? "max-w-xs" : "flex-1 max-w-xs"}`}>
                <div className={`p-3  mr-4 dark:bg-[#0f172a] bg-[#f8fafc]  ${isOwn ? "message-bubble-own shadow-[4px_4px_0_0_var(--bubble-own-from)] rounded-tl-[30px] rounded-bl-[30px] rounded-br-[30px]" : "message-bubble-other shadow-[4px_4px_0_0_var(--bubble-own-to)] rounded-tr-[30px] rounded-bl-[30px] rounded-br-[30px]"}`}>
                    {/* Sender name header — matches text message layout */}
                    <div className="flex items-center justify-between mb-2 gap-3">
                       <span
  className={`text-[14px] ${
    isOwn ? "text-[var(--bubble-own-from)]" : "text-[var(--bubble-own-to)]"
  }`}
>
  {isOwn ? "You" : (message?.senderName || "Anonymous")}
</span>
                        {isOwn && onDelete && (
                            <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                    <Button
                                        variant="ghost"
                                        size="icon"
                                        className="w-6 h-6 p-0 text-[var(--bubble-own-from)] hover:bg-[var(--bubble-own-from)] hover:text-[#faf8fc]"
                                    >
                                        <ChevronDown className="w-4 h-4" />
                                    </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end">
                                    <DropdownMenuItem
                                        onSelect={(e) => {
                                            e.preventDefault();
                                            setConfirmDeleteOpen(true);
                                        }}
                                        className="justify-center hover:text-white cursor-pointer"
                                    >
                                        Delete
                                    </DropdownMenuItem>
                                </DropdownMenuContent>
                            </DropdownMenu>
                        )}
                    </div>
                    <div className="flex items-center space-x-3">
                        <Button
                            size="icon"
                            variant={isOwn ? "secondary" : "default"}
                            className="w-9 h-9 rounded-full flex-shrink-0"
                            onClick={togglePlay}
                            disabled={!audioUrl}
                        >
                            {isPlaying ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4 ml-0.5" />}
                        </Button>
                        <div className="flex-1 min-w-0">
                            {/* Waveform bars */}
                            <div className="flex items-end space-x-[2px]" style={{ height: `${maxBarH + 4}px` }}>
                                {bars.map((h, i) => {
                                    const filled = i / barCount <= progress;
                                    return (
                                        <div
                                            key={i}
                                            className={`w-[3px] rounded-full flex-shrink-0 transition-colors duration-100 ${
                                                filled
                                                    ? isOwn ? "bg-[var(--bubble-own-from)]" : "bg-[var(--bubble-own-to)]"
                                                    : isOwn ? "bg-[var(--bubble-own-from)]/30" : "bg-gray-300 dark:bg-gray-600"
                                            }`}
                                            style={{ height: `${h}px` }}
                                        />
                                    );
                                })}
                            </div>
                            {/* Audio time (current position or total duration) */}
                            <span className={`text-xs mt-1 block tabular-nums ${isOwn ? "opacity-60 text-[var(--bubble-own-from)]" : "text-[var(--bubble-own-to)]"}`}>
                                {currentTimeLabel}
                            </span>
                        </div>
                    </div>

                    {message.content?.trim() && (
                        <p className="text-sm mt-2 italic opacity-80">&ldquo;{message.content}&rdquo;</p>
                    )}

                    {/* Message send time + delivery tick */}
                    <div className="flex items-center justify-end gap-2 mt-2">
                        <span className={`text-xs text-[var(--accent)] ${isOwn ? "opacity-60 text-[var(--bubble-own-from)]" : "text-[var(--bubble-own-to)]"}`}>
                            {formatTime(message.timestamp)}
                        </span>
                        {isOwn && (
                            <span
                                title={
                                    (message.readBy && message.readBy.some((uid) => uid !== message.senderId)) || message.deliveryStatus === 'read'
                                        ? "Seen"
                                        : "Delivered"
                                }
                            >
                                <CheckCheck
                                    className={`w-5 h-5 mb-1 transition-colors duration-300 ${
                                        (message.readBy && message.readBy.some((uid) => uid !== message.senderId)) || message.deliveryStatus === 'read'
                                            ? "text-[var(--bubble-own-from)] opacity-100"
                                            : "text-gray-400 dark:text-gray-500 opacity-60"
                                    }`}
                                    aria-label={
                                        (message.readBy && message.readBy.some((uid) => uid !== message.senderId)) || message.deliveryStatus === 'read'
                                            ? "Seen"
                                            : "Delivered"
                                    }
                                />
                            </span>
                        )}
                    </div>
                </div>
            </div>
            <DeleteMessageDialog
                open={confirmDeleteOpen}
                onOpenChange={setConfirmDeleteOpen}
                onConfirm={() => {
                    setConfirmDeleteOpen(false);
                    onDelete?.();
                }}
            />
        </div>
    );
}
